import type { AttackKind, Combatant, Intent } from './simulation'

export const OMEGA_VARIANTS=['neutral','rush','depth','counter','punish','pressure','guard-breaker','ender'] as const
export type OmegaVariant=typeof OMEGA_VARIANTS[number]
export const OMEGA_HIT_OUTCOMES=['light-flinch','medium-stagger','heavy-stagger','crush','counter-hit','punish-hit','guard-hit','guard-crush','parry','throw','knockdown','wall-splat'] as const
export type OmegaHitOutcome=typeof OMEGA_HIT_OUTCOMES[number]
export const OMEGA_SKILL_FAMILIES=[
 'strike','launcher','breaker','reversal','grapple','throw','projectile','beam','rush','dash-strike','teleport','blink',
 'counter','parry','armor','guard','stance','install','transform','flight','dive','trap','mine','summon','clone','field',
 'aura','heal','drain','buff','debuff','control','super','ultimate','finisher','environment'
] as const
export type OmegaSkillFamily=typeof OMEGA_SKILL_FAMILIES[number]

export type OmegaTuning={damage:number;guardDamage:number;hitstun:number;blockstun:number;knockback:number;knockdown:number}

const VARIANT_TUNING:Record<OmegaVariant,OmegaTuning>={
 neutral:{damage:1,guardDamage:1,hitstun:1,blockstun:1,knockback:1,knockdown:1},
 rush:{damage:1.04,guardDamage:.96,hitstun:.96,blockstun:.94,knockback:1.12,knockdown:.96},
 depth:{damage:.98,guardDamage:1.02,hitstun:1.04,blockstun:1.04,knockback:.94,knockdown:1},
 counter:{damage:1.12,guardDamage:1.08,hitstun:1.14,blockstun:1.06,knockback:1.08,knockdown:1.08},
 punish:{damage:1.16,guardDamage:1.1,hitstun:1.18,blockstun:1.08,knockback:1.1,knockdown:1.1},
 pressure:{damage:.94,guardDamage:1.14,hitstun:.96,blockstun:1.12,knockback:.9,knockdown:.92},
 'guard-breaker':{damage:.9,guardDamage:1.34,hitstun:1.04,blockstun:1.18,knockback:1.04,knockdown:1},
 ender:{damage:1.08,guardDamage:1.16,hitstun:1.12,blockstun:1.1,knockback:1.24,knockdown:1.18},
}

const SUFFIX:Record<OmegaVariant,string>={
 neutral:'',rush:'Rush',depth:'Depth',counter:'Counter',punish:'Punish',pressure:'Pressure',
 'guard-breaker':'Guard Breaker',ender:'Omega Ender',
}

export function comboDamageScale(comboCount:number){
 if(!Number.isFinite(comboCount)||comboCount<=1)return 1
 return Math.max(.5,1-Math.min(comboCount-1,8)*.075)
}

export function omegaVariantForCombo(comboCount:number,kind:AttackKind):OmegaVariant{
 if(comboCount>=4)return'ender'
 if(comboCount>=2)return'pressure'
 if(kind==='heavy')return'guard-breaker'
 return'neutral'
}

export function resolveOmegaVariant(kind:AttackKind,attacker:Combatant,defender:Combatant,intent:Intent):OmegaVariant{
 if(defender.attackPhase==='recovery')return'punish'
 if(defender.attackPhase==='startup'||defender.attackPhase==='active')return'counter'
 if(attacker.comboCount>=4)return'ender'
 if(defender.blocking||defender.guardMeter<34)return'guard-breaker'
 if(attacker.comboCount>=2)return'pressure'
 if(intent.depthUp||intent.depthDown||Math.abs(defender.z-attacker.z)>.48)return'depth'
 if(intent.dash||intent.quantum||attacker.quantumTimer>0||attacker.aerialMode!=='ground')return'rush'
 if(kind==='heavy'&&defender.guardMeter<55)return'guard-breaker'
 return'neutral'
}

export function omegaVariantTuning(kind:AttackKind,variant:OmegaVariant,comboCount:number):OmegaTuning{
 const base=VARIANT_TUNING[variant]
 const deep=Math.max(0,Math.min(comboCount-2,5))
 const kindKnockback=kind==='special'?1.08:kind==='throw'?1.04:1
 return{
  damage:base.damage,
  guardDamage:base.guardDamage,
  hitstun:Math.max(.88,base.hitstun-deep*.015),
  blockstun:base.blockstun,
  knockback:base.knockback*kindKnockback,
  knockdown:base.knockdown,
 }
}

export function canOmegaCancel(from:AttackKind,to:AttackKind,comboCount:number){
 if(comboCount<0||comboCount>32)return false
 if(from==='light')return to==='heavy'||to==='special'||to==='throw'
 if(from==='heavy')return to==='special'||to==='throw'||(to==='light'&&comboCount<=2)
 if(from==='throw')return to==='special'&&comboCount<=2
 if(from==='special')return to==='light'&&comboCount>0&&comboCount<4
 return false
}

export function classifyOmegaHitOutcome(
 kind:AttackKind,variant:OmegaVariant,blocked:boolean,guardBroken:boolean,knockdown:boolean,nearWall:boolean,comboCount:number
):OmegaHitOutcome{
 if(blocked)return guardBroken?'guard-crush':'guard-hit'
 if(kind==='throw')return'throw'
 if(nearWall&&(kind==='heavy'||kind==='special'||variant==='ender'))return'wall-splat'
 if(knockdown)return'knockdown'
 if(variant==='punish')return'punish-hit'
 if(variant==='counter')return'counter-hit'
 if(variant==='guard-breaker'||variant==='ender')return'crush'
 if(kind==='special')return'heavy-stagger'
 if(kind==='heavy'||comboCount>=3)return'medium-stagger'
 return'light-flinch'
}

export function omegaSkillVariantName(baseName:string,variant:OmegaVariant){
 const suffix=SUFFIX[variant]
 return suffix?baseName+' · '+suffix:baseName
}
